import {
  inject,
  Injectable,
  DestroyRef,
  Signal,
  computed,
} from '@angular/core';
import { SubmissionsService } from './submissions.service';
import { PermissionsService } from '@shared/services/permissions.service';
import {
  combineLatest,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  BasePermission,
  PermissionGrants,
  QuestionnaireAccess,
  RolePermission,
  SiteSubmission,
  StudySubmission,
  SubmissionDocumentAccess,
  SubmissionSuperAdmin,
  UpdateSubmissionPriority,
  UpdateSubmissionTeam,
} from '@shared/interfaces/permissions.interface';
import { SubmissionDetailView } from '../models/submission-detail-view.model';
import { DocumentsService } from '@documents/services/documents.service';
import { WebSocketService } from '../../../websocket/services/web-socket.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { UserRole } from '@data-access/roles';
import { UtilsService } from '@shared/services/utils.service';
import { convertSnakeToCamelCase } from '@shared/utils';
import {
  ChatData,
  generateChatKey,
  SubmissionChatData,
} from '@data-access/talk-desk/types/talkdesk.type';
import { IUser } from '@shared/user.interface';
import environment from '../../../../environments/environment';
import { closeActiveThread } from '../redux/submission.actions';

import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TALKDESK_ACTIONS } from '@data-access/talk-desk';
import { TeamManagementService } from '@team-management/services/team-management.service';
import { SectionAssignmentContact, Submission } from '../models';

@Injectable()
export class SubmissionDetailFacadeService {
  private readonly _utils = inject(UtilsService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _submissionService = inject(SubmissionsService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _documentService = inject(DocumentsService);
  private readonly _teamManagementService = inject(TeamManagementService);
  private readonly _webSocketService = inject(WebSocketService);
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  subViewModel$ = new Subject<SubmissionDetailView>();
  refreshSubject = new Subject<void>();

  roles$ = this._submissionService.entityType$.pipe(
    switchMap((entityType) => this._teamManagementService.getRoles(entityType)),
  );

  $submission = toSignal(
    this._submissionService.submission$,
  ) as Signal<Submission>;

  submissionDetail$: Observable<SubmissionDetailView> =
    this.refreshSubject.pipe(
      startWith(void 0),
      switchMap(() =>
        combineLatest([
          this._submissionService.submission$,
          this._submissionService.hasNotificationsDetails$,
          this._submissionService.hasNotificationsDocuments$,
          this._submissionService.notificationsDocumentsCount$,
          this._permissionsService.isInternal$,
          this.roles$,
        ]).pipe(
          mergeMap(
            ([
              submission,
              hasNotificationsDetails,
              hasNotificationsDocuments,
              notificationsDocumentsCount,
              isInternal,
              roles,
            ]) => {
              this._permissionsService.setEntityRolesAndPermission(
                submission.id,
                submission.role,
              );
              /**
               * @QUESTION
               * Why do we need todo a multi layers observables.
               * And why are we list to a Subject() and then -> switchMapping -> combineLatest -> mergeMapping -> combineLatest the observables..?
               * */
              return combineLatest({
                hasNotificationsDetails: of(hasNotificationsDetails ?? false),
                hasNotificationsDocuments: of(
                  hasNotificationsDocuments ?? false,
                ),
                isInternal: of(isInternal ?? false),
                notificationsDocumentsCount: of(
                  notificationsDocumentsCount ?? 0,
                ),
                roles: of(roles),
                submission: of(submission),
                workspaceId: this._webSocketService.subscribeToWorkspace(
                  submission.id,
                ),
              });
            },
          ),
        ),
      ),
    );

  /**
   * Determines if the workspace stage is being updated. Used primarily for
   * locking down collab-hub feature(s) which may depend on the workspace stage.
   */
  $submissionStageLoading = computed(() =>
    this._submissionService.$stageLoading(),
  );

  /**
   * @This is required for updating
   * the canRemove effectively on header component.
   * */
  $canRemove = toSignal(
    this.submissionDetail$.pipe(
      filter(Boolean),
      switchMap((s) => this.getCanDeleteSubmission(s)),
    ),
  ) as Signal<boolean>;

  $canEdit = toSignal(
    this.getSubmissionPermissions(
      [SiteSubmission, StudySubmission],
      [PermissionGrants.CanEdit],
    ),
  ) as Signal<boolean>;

  $canViewSubmissionSites = toSignal(
    this.getSubmissionPermissions(
      [SiteSubmission, StudySubmission],
      [PermissionGrants.CanView],
    ),
  ) as Signal<boolean>;

  // ERM-4993 - The getSubmissionPermissions did not compute this correctly, but the questionnaire endpoint already returns this permission
  $canViewSubmissionDetails = toSignal(
    this.submissionDetail$.pipe(
      filter(Boolean),
      map((s) => {
        return s.submission.permissions?.can_view_submission_details;
      }),
    ),
  ) as Signal<boolean>;

  $canEditSubmissionDetails = toSignal(
    this.getSubmissionPermissions(
      [QuestionnaireAccess],
      [PermissionGrants.CanEdit],
    ),
  ) as Signal<boolean>;

  $canViewSubmissionDocuments = toSignal(
    this.getSubmissionPermissions(
      [SubmissionDocumentAccess],
      [PermissionGrants.CanView],
    ),
  ) as Signal<boolean>;

  $canUpdateSubmissionPriority = toSignal(
    this.getSubmissionPermissions(
      [UpdateSubmissionPriority],
      [PermissionGrants.CanEdit],
    ),
  ) as Signal<boolean>;

  $canUpdateSubmissionTeam = toSignal(
    this.getSubmissionPermissions(
      [UpdateSubmissionTeam],
      [PermissionGrants.CanEdit],
    ),
  ) as Signal<boolean>;

  $submissionDetail = toSignal(
    this.submissionDetail$,
  ) as Signal<SubmissionDetailView>;

  showLeaveModal$: Observable<{
    display: boolean;
    nextUrl?: string;
  } | null> = this._documentService.displayModal$.asObservable();

  constructor() {
    /**
     * @TODO Need TO OPTIMIZE THIS FOR PERFORMANCE
     * Limiting factor is the subscriptions inside
     * workspace component
     * */
    this.submissionDetail$?.subscribe((details) => {
      this.subViewModel$.next(details);
    });
  }

  /**
   * Load the submission detail
   */
  getSubmissionDetail(submissionId: string) {
    return this._submissionService.getSubmission(submissionId);
  }

  /**
   * Get permissions for a submission
   * TODO: Once the use-case is clear, this should be refactored to select the proper grants, etc. And can be declared as an obserable property on the service
   */
  // Change how we are getting the permissions for a submission
  getSubmissionPermissions(
    permissions: BasePermission[],
    grants: PermissionGrants[],
  ) {
    return this._submissionService.submission$.pipe(
      map((submission) => {
        const subMissionRole = this._utils.convertKeysToCamelCase(
          submission.role,
        );
        return grants.every((userGrant: PermissionGrants) => {
          const formattedUserGrant = convertSnakeToCamelCase(userGrant);
          if ((subMissionRole as UserRole)?.permissions) {
            return subMissionRole.permissions.some(
              (perm: RolePermission) =>
                perm[formattedUserGrant as keyof RolePermission],
            );
          }
          return false;
        });
      }),
    );
  }

  updateSubmissionName(id: string, name: string, data: any): void {
    const payload = {
      data: data,
      name: name,
    };
    this._submissionService.updateSubmission(id, payload);
  }

  getCanDeleteSubmission(viewModel: SubmissionDetailView): Observable<boolean> {
    return combineLatest([
      // Check remove permission for the submission
      this._permissionsService.observeEntityPermissions(
        viewModel.submission.id,
        [StudySubmission, SiteSubmission],
        [PermissionGrants.CanRemove],
      ),
      // Check edit permission for the submission
      this._permissionsService.observeEntityPermissions(
        viewModel.submission.id,
        [StudySubmission, SiteSubmission],
        [PermissionGrants.CanEdit],
      ),
      // Check user permissions
      this._permissionsService.observeUserPermissions(
        [SubmissionSuperAdmin],
        [PermissionGrants.CanRemove],
      ),

      // Check if the user is internal
      this._permissionsService.isInternal$,
    ]).pipe(
      map(
        ([removePermission, editPermissions, userPermissions, isInternal]) => {
          // IF the user has submission super admin permission, they can always delete the submission
          if (userPermissions) {
            return true;
          }

          // If the submission is in draft stage and the user has edit or remove permission, they can delete the submission
          if (
            viewModel.submission.current_stage === 'draft' &&
            (removePermission || editPermissions)
          ) {
            return true;
          }

          const grantedPermissions =
            viewModel.submission.role?.permissions?.some((p) => {
              const canRemove = 'can_remove' in p ? p.can_remove : p.canRemove;
              const canEdit = 'can_edit' in p ? p.can_edit : p.canEdit;
              return canEdit || canRemove;
            });

          if (
            viewModel.submission.current_stage === 'draft' &&
            grantedPermissions
          ) {
            return true;
          }

          // If the submission is in qc_check stage and the user is internal with remove permission, they can delete the submission
          if (
            viewModel.submission.current_stage === 'qc_check' &&
            isInternal &&
            (removePermission || userPermissions)
          ) {
            return true;
          }

          return false;
        },
      ),
    );
  }

  updateStage(workspaceId: string) {
    return this._submissionService.updateWorkspaceStage(workspaceId).pipe(
      tap(() => {
        // close active thread has a side effect to
        // refetch all threads.  which, when done, a user
        // can re-open a closed sidebar thread and see that
        // the new status is reflected in the sidebar
        this._store.dispatch(closeActiveThread());
      }),
    );
  }

  loadSubmission(submissionId: string) {
    this._submissionService.loadSubmission(submissionId);
  }

  /*
   * @WHERE is this refreshViewModel() is getting called?
   * */
  refreshViewModel() {
    this.refreshSubject.next();
  }

  cancelAllUploads() {
    this._submissionService.submission$
      ?.pipe(take(1))
      ?.subscribe((submission) => {
        this._documentService.cancelAllEntityUploads(submission.id);
      });
  }

  dismissLeaveModal(url?: string) {
    this._documentService.dismissUploadInProgressModal();

    if (url) {
      this._documentService.removeUploadingDocuments();

      this._router.navigateByUrl(url);
    }
  }

  openChat(user: IUser) {
    const _baseAppUrl =
      'baseAppUrl' in environment ? environment.baseAppUrl : '';
    this._submissionService.submission$
      ?.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((submission) => {
        const chatData: SubmissionChatData = {
          primaryId: submission.id,
          contextType: 'submission',
          isOpen: true,
          submissionId: submission.id,
          submissionTitle: submission.name,
          user,
          question: `I have a question about <a href="${_baseAppUrl}/submissions/${submission.int_id}">${submission.int_id}</a>`,
        };

        const chatId = generateChatKey('submission', submission.id);
        this._store.dispatch(TALKDESK_ACTIONS.openChat({ chatId, chatData }));
      });
  }

  setTalkdeskContext(chatData: ChatData) {
    this._submissionService.submission$
      ?.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((submission) => {
        const chatId = generateChatKey('submission', submission.id);
        this._store.dispatch(TALKDESK_ACTIONS.setContext({ chatId, chatData }));
      });
  }

  reloadSubmission(submissionId: string) {
    this._submissionService.loadSubmission(submissionId);
  }

  getSubmissionTeam(
    submissionId: string,
  ): Observable<SectionAssignmentContact[]> {
    return this._submissionService.loadSubmissionTeam(submissionId);
  }

  assignSection(
    workspaceId: string,
    sectionId: string,
    userId?: string | null,
  ) {
    return this._submissionService.assignSection(
      workspaceId,
      sectionId,
      userId,
    );
  }
}
